import { useQuery } from "@tanstack/react-query";
import { RefObject, useEffect, useRef, useState } from "react";
import {
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  useCombobox,
} from "downshift";
import { hentAdresse, soegAdresser } from "../../api/adressevaelger";
import {
  AdressevaelgerAdresse,
  AdressevaelgerFund,
} from "../../interfaces/adressevaelger";
import SuperDropdown from "../dropdown/SuperDropdown";
import DefaultDropdownItem from "../dropdown/DefaultDropdownItem";
import SearchIcon from "../icons/SearchIcon";
import Spinner from "../spinner/Spinner";
import useDebounce from "../../hooks/useDebounce";

type AdresseItem = AdressevaelgerFund & { id: string };

/** Kun `adresse` er et endeligt valg. Vælges et delvist fund (vejnavn,
 * husnummer, ...), skrives dets titel i feltet, og søgningen fortsætter. */
const erDelvistFund = (item: AdresseItem | null | undefined) =>
  !!item && item.type !== "adresse";

/** Teksten, der skrives i feltet, når et fund vælges. For vej+postnummer
 * efterlades en plads til husnummeret (`Bredgade , 1260 København K`), som
 * markøren sættes i — API'et finder kun husnumre før postnummeret. */
function itemToString(item: AdresseItem | null): string {
  if (!item) return "";
  const { type, titel, vejnavn, postnr, postdistrikt } = item;
  if (type === "navngivenvejpostnummer" && vejnavn && postnr) {
    return `${vejnavn} , ${postnr} ${postdistrikt ?? ""}`.trim();
  }
  if (type === "vejnavn") return `${titel} `;
  return titel;
}

export default function AutoCompleteAddress({
  onSelectedItemChange,
  selectedItem,
  classNameContainer,
  onBlur,
  focusOnMount,
}: {
  /** Kaldes med den fulde DAR-adresse, når brugeren har valgt en adresse. */
  onSelectedItemChange: (adresse: AdressevaelgerAdresse) => void;
  selectedItem: AdressevaelgerAdresse | undefined;
  classNameContainer?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  focusOnMount?: boolean;
}) {
  // state
  const [inputValue, setInputValue] = useState("");
  const [isHentning, setIsHentning] = useState(false);
  const debouncedInputValue = useDebounce(inputValue, 500);

  // fetching stuff
  const { data, isFetching } = useQuery<AdresseItem[]>({
    queryKey: ["adressevaelger-soeg", debouncedInputValue],
    queryFn: async () =>
      (await soegAdresser(debouncedInputValue)).map((fund, index) => ({
        ...fund,
        // vejnavn-fund har intet id
        id: fund.id ?? `${fund.type}-${index}`,
      })),
    enabled: debouncedInputValue.trim().length > 0,
  });
  const isPending = isFetching || isHentning;

  // ref
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [selectedItem, debouncedInputValue]);

  useEffect(() => {
    const inputElement = inputRef.current;
    if (caretRef.current !== null && inputElement) {
      inputElement.setSelectionRange(caretRef.current, caretRef.current);
      caretRef.current = null;
    }
  }, [inputValue]);

  // handlers
  function onStateChangeHandler(changes: UseComboboxStateChange<AdresseItem>) {
    if (changes.inputValue !== undefined) {
      const inputValue = changes.inputValue || "";
      const { selectedItem } = changes;
      if (
        selectedItem?.type === "navngivenvejpostnummer" &&
        selectedItem.vejnavn &&
        inputValue.startsWith(`${selectedItem.vejnavn} , `)
      ) {
        caretRef.current = selectedItem.vejnavn.length + 1;
      }
      setInputValue(inputValue);
      inputRef.current?.focus();
    }
  }

  async function onSelectedItemEventHandler(
    event: UseComboboxStateChange<AdresseItem>,
  ) {
    const item = event.selectedItem;
    if (!item || item.type !== "adresse") return;
    // Søgefundet har ingen strukturerede felter — hent adressen på id.
    setIsHentning(true);
    try {
      const adresse = await hentAdresse(item.id);
      if (adresse) onSelectedItemChange(adresse);
    } catch (error) {
      console.error("Kunne ikke hente adressen fra Adressevælgeren", error);
    } finally {
      setIsHentning(false);
    }
  }

  function stateReducerHandler(
    state: UseComboboxState<AdresseItem>,
    actionAndChanges: UseComboboxStateChangeOptions<AdresseItem>,
  ) {
    const { type, changes } = actionAndChanges;
    switch (type) {
      case useCombobox.stateChangeTypes.InputKeyDownEnter:
      case useCombobox.stateChangeTypes.ItemClick:
        return {
          ...changes,
          isOpen: erDelvistFund(changes.selectedItem) ? true : changes.isOpen,
        };
      case useCombobox.stateChangeTypes.InputBlur:
        return {
          ...changes,
          selectedItem: state.selectedItem,
          inputValue: state.inputValue,
        };
    }
    return { ...changes };
  }

  return (
    <SuperDropdown
      asDropdown={false}
      classNameContainer={classNameContainer}
      items={debouncedInputValue.trim() ? data : []}
      itemsIdKey={"id"}
      itemToString={itemToString}
      itemToElement={(item) => <DefaultDropdownItem label={item.titel} />}
      initialInputValue={debouncedInputValue}
      inputRef={inputRef as RefObject<HTMLInputElement>}
      placeholder="Søg og vælg adresse ..."
      closedIcon={isPending ? <Spinner inline size={14} /> : <SearchIcon />}
      openIcon={isPending ? <Spinner inline size={14} /> : <SearchIcon />}
      onStateChange={(changes) => onStateChangeHandler(changes)}
      onSelectedItemEvent={(event) => void onSelectedItemEventHandler(event)}
      stateReducer={(state, actionAndChanges) =>
        stateReducerHandler(state, actionAndChanges)
      }
      onBlur={onBlur}
      focusInputOnMount={focusOnMount}
    />
  );
}
